<template>
  <div class="proposal-stats">
    <StatsBar
      v-model:period="selectedPeriod"
      :loading="loading"
      @period-change="handlePeriodChange"
      @date-range-change="handleDateRangeChange"
    >
      <StatsCard
      :label="$t('proposals.stats.total_sent')"
      :value="stats.total_sent_value"
      format="currency"
      :trend="trends.total_sent_value"
      :loading="loading"
    />

    <StatsCard
      :label="$t('proposals.stats.pending')"
      :value="stats.pending_value"
      format="currency"
      :trend="trends.pending_count"
      :loading="loading"
    />

    <StatsCard
      :label="$t('proposals.stats.accepted')"
      :value="stats.accepted_value"
      format="currency"
      :trend="trends.accepted_count"
      :loading="loading"
    />

    <StatsCard
      :label="$t('proposals.stats.rejected')"
      :value="stats.rejected_count"
      :trend="trends.rejected_count"
      :trend-inverted="true"
      :loading="loading"
    />

    <StatsCard
      :label="$t('proposals.stats.conversion_rate')"
      :value="conversionRatePercent"
      format="percentage"
      :trend="trends.conversion_rate"
      :loading="loading"
    />
    </StatsBar>

    <div v-if="!loading && rejectionReasons.length" class="proposal-stats__reasons">
      <h3 class="proposal-stats__reasons-title">{{ $t('proposals.stats.reject_reasons') }}</h3>
      <div
        v-for="row in rejectionReasons"
        :key="row.code"
        class="proposal-stats__reason"
      >
        <div class="proposal-stats__reason-meta">
          <span class="proposal-stats__reason-label">{{ reasonLabel(row.code) }}</span>
          <span class="proposal-stats__reason-value">
            {{ row.count }} · {{ formatReasonValue(row.value) }}
          </span>
        </div>
        <div class="proposal-stats__reason-track">
          <div
            class="proposal-stats__reason-fill"
            :style="{ width: reasonPercent(row) + '%' }"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  /**
   * ProposalStats Component
   *
   * Displays proposal statistics with period selector and trend indicators.
   * Uses the reusable StatsBar and StatsCard components.
   *
   * @example
   * <ProposalStats @stats-loaded="handleStatsLoaded" />
   */
  import { defineComponent, ref, computed, onMounted, watch, getCurrentInstance } from 'vue';
  import dayjs from 'dayjs';
  import { StatsBar, StatsCard } from '@/components/partials/stats';
  import { crmService } from '@/services/crmService';

  export default defineComponent({
    name: 'ProposalStats',

    components: {
      StatsBar,
      StatsCard,
    },

    props: {
      /**
       * Initial period to load
       */
      initialPeriod: {
        type: String,
        default: '30d',
      },
    },

    emits: ['stats-loaded', 'period-change'],

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance?.appContext?.config?.globalProperties?.$t || ((key) => key);

      const selectedPeriod = ref(props.initialPeriod);
      const loading = ref(true);

      const customDateFrom = ref(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
      const customDateTo = ref(dayjs().format('YYYY-MM-DD'));

      // Stats data
      const stats = ref({
        total: 0,
        total_sent_value: 0,
        pending_count: 0,
        pending_value: 0,
        accepted_count: 0,
        accepted_value: 0,
        rejected_count: 0,
        rejected_value: 0,
        conversion_rate: 0,
        rejection_reasons: [],
      });

      const trends = ref({
        total: null,
        total_sent_value: null,
        pending_count: null,
        accepted_count: null,
        rejected_count: null,
        conversion_rate: null,
      });

      const ytdStats = ref({
        total: 0,
        accepted_count: 0,
        accepted_value: 0,
        conversion_rate: 0,
      });

      // Computed
      const conversionRatePercent = computed(() => {
        return Math.round((stats.value.conversion_rate || 0) * 100);
      });

      const rejectionReasons = computed(() => stats.value.rejection_reasons || []);

      const rejectionTotal = computed(() =>
        rejectionReasons.value.reduce((sum, row) => sum + (row.count || 0), 0)
      );

      const reasonLabel = (code) => {
        const key = `proposals.reject_reasons.${code}`;
        const translated = $t(key);
        return translated === key ? $t('proposals.reject_reasons.unknown') : translated;
      };

      const reasonPercent = (row) => {
        if (!rejectionTotal.value) return 0;
        return Math.round(((row.count || 0) / rejectionTotal.value) * 100);
      };

      const formatReasonValue = (value) => {
        return new Intl.NumberFormat('nl-NL', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value || 0);
      };

      // Format currency
      const formatCurrency = (value) => {
        if (value === null || value === undefined || value === 0) {
          return null;
        }
        return new Intl.NumberFormat('nl-NL', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value);
      };

      // Format count with label
      const formatCount = (count) => {
        if (count === null || count === undefined) {
          return null;
        }
        return `${count} ${count === 1 ? $t('proposals.stats.proposal_singular') : $t('proposals.stats.proposal_plural')}`;
      };

      // Load stats from API (or calculate client-side as fallback)
      const loadStats = async (period) => {
        loading.value = true;

        try {
          const params = { period };
          if (period === 'custom') {
            params.date_from = customDateFrom.value;
            params.date_to = customDateTo.value;
          }

          const response = await crmService.getProposalStats(params);

          if (response && response.current) {
            stats.value = response.current;
            trends.value = response.trends || {};
            ytdStats.value = response.year_to_date || {};
          } else {
            await calculateStatsClientSide(period);
          }

          emit('stats-loaded', { period, stats: stats.value, trends: trends.value });
        } catch (error) {
          console.warn('Failed to fetch proposal stats from API, calculating client-side:', error);
          await calculateStatsClientSide(period);
        } finally {
          loading.value = false;
        }
      };

      // Client-side calculation fallback
      const calculateStatsClientSide = async (period) => {
        try {
          // Fetch all proposals (without status filter)
          const response = await crmService.getOrganizationProposals({
            page: 1,
            limit: 1000, // Get all for calculation
          });

          const proposals = response.proposals || [];
          const now = new Date();
          const periodEnd = period === 'custom'
            ? new Date(customDateTo.value + 'T23:59:59')
            : now;

          // Calculate period boundaries
          const { start: periodStart, previousStart, previousEnd } = getPeriodDates(period, now);

          // Filter proposals by period
          const currentPeriodProposals = proposals.filter((p) => {
            const createdAt = new Date(p.created_at);
            return createdAt >= periodStart && createdAt <= periodEnd;
          });

          const previousPeriodProposals = previousStart
            ? proposals.filter((p) => {
                const createdAt = new Date(p.created_at);
                return createdAt >= previousStart && createdAt <= previousEnd;
              })
            : [];

          // Calculate current period stats
          const currentStats = calculatePeriodStats(currentPeriodProposals);
          const previousStats = calculatePeriodStats(previousPeriodProposals);

          stats.value = currentStats;

          // Calculate trends
          if (previousStats.total > 0) {
            trends.value = {
              total: calculateTrendPercent(currentStats.total, previousStats.total),
              total_sent_value: calculateTrendPercent(
                currentStats.total_sent_value,
                previousStats.total_sent_value
              ),
              pending_count: calculateTrendPercent(
                currentStats.pending_count,
                previousStats.pending_count
              ),
              accepted_count: calculateTrendPercent(
                currentStats.accepted_count,
                previousStats.accepted_count
              ),
              rejected_count: calculateTrendPercent(
                currentStats.rejected_count,
                previousStats.rejected_count
              ),
              conversion_rate: calculateTrendPercent(
                currentStats.conversion_rate * 100,
                previousStats.conversion_rate * 100
              ),
            };
          } else {
            trends.value = {
              total: null,
              total_sent_value: null,
              pending_count: null,
              accepted_count: null,
              rejected_count: null,
              conversion_rate: null,
            };
          }

          // Calculate YTD stats
          const yearStart = new Date(now.getFullYear(), 0, 1);
          const ytdProposals = proposals.filter((p) => new Date(p.created_at) >= yearStart);
          ytdStats.value = calculatePeriodStats(ytdProposals);
        } catch (error) {
          console.error('Failed to calculate stats client-side:', error);
        }
      };

      // Helper: Get period date boundaries
      const getPeriodDates = (period, now) => {
        let start, previousStart, previousEnd;

        switch (period) {
          case '30d':
            start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            previousEnd = new Date(start.getTime() - 1);
            previousStart = new Date(start.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          case '90d':
            start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            previousEnd = new Date(start.getTime() - 1);
            previousStart = new Date(start.getTime() - 90 * 24 * 60 * 60 * 1000);
            break;
          case 'ytd':
            start = new Date(now.getFullYear(), 0, 1);
            previousEnd = new Date(now.getFullYear() - 1, 11, 31);
            previousStart = new Date(now.getFullYear() - 1, 0, 1);
            break;
          case 'custom': {
            const from = new Date(customDateFrom.value);
            const to = new Date(customDateTo.value);
            const duration = to.getTime() - from.getTime();
            start = from;
            previousEnd = new Date(from.getTime() - 1);
            previousStart = new Date(from.getTime() - duration);
            break;
          }
          case 'all':
          default:
            start = new Date(0);
            previousStart = null;
            previousEnd = null;
            break;
        }

        return { start, previousStart, previousEnd };
      };

      // Helper: Get netto value (excl. BTW) with backward compatibility
      const getNettoValue = (proposal) => {
        const total = proposal.total_amount || 0;

        // If prices_include_vat is false, total_amount is already excl. BTW
        if (proposal.prices_include_vat === false) {
          return total;
        }

        // If prices_include_vat is true (or undefined for backward compat),
        // use subtotal_excl_vat if available, otherwise calculate
        if (proposal.subtotal_excl_vat != null) {
          return proposal.subtotal_excl_vat;
        }

        // Fallback: calculate from total_amount using vat_rate (default 21%)
        const vatRate = proposal.vat_rate ?? 21;
        return Math.round((total / (1 + vatRate / 100)) * 100) / 100;
      };

      // Helper: Calculate stats for a set of proposals (using netto values excl. BTW)
      // Pro forma proposals are excluded from revenue stats unless no definitive exists
      const calculatePeriodStats = (proposals) => {
        const pending = proposals.filter((p) => ['sent', 'viewed'].includes(p.status));
        const accepted = proposals.filter((p) => p.status === 'accepted');
        const rejected = proposals.filter((p) => p.status === 'rejected');
        const sent = proposals.filter((p) => ['sent', 'viewed', 'accepted', 'rejected'].includes(p.status));

        // For accepted value: prefer definitive, fallback to proforma if no definitive
        const acceptedDefinitive = accepted.filter(
          (p) => !p.proposal_type || p.proposal_type === 'definitive'
        );
        const acceptedForRevenue = acceptedDefinitive.length > 0
          ? acceptedDefinitive
          : accepted;

        const totalSentValue = sent.reduce((sum, p) => sum + getNettoValue(p), 0);
        const pendingValue = pending.reduce((sum, p) => sum + getNettoValue(p), 0);
        const acceptedValue = acceptedForRevenue.reduce((sum, p) => sum + getNettoValue(p), 0);
        const rejectedValue = rejected.reduce((sum, p) => sum + getNettoValue(p), 0);

        const decided = accepted.length + rejected.length;
        const conversionRate = decided > 0 ? accepted.length / decided : 0;

        return {
          total: proposals.length,
          total_sent_value: totalSentValue,
          pending_count: pending.length,
          pending_value: pendingValue,
          accepted_count: accepted.length,
          accepted_value: acceptedValue,
          rejected_count: rejected.length,
          rejected_value: rejectedValue,
          conversion_rate: conversionRate,
          rejection_reasons: [],
        };
      };

      // Helper: Calculate trend percentage
      const calculateTrendPercent = (current, previous) => {
        if (previous === 0) return current > 0 ? 100 : 0;
        return Math.round(((current - previous) / previous) * 100);
      };

      const handlePeriodChange = (period) => {
        emit('period-change', period);
        loadStats(period);
      };

      const handleDateRangeChange = (range) => {
        if (range?.start && range?.end) {
          customDateFrom.value = dayjs(range.start).format('YYYY-MM-DD');
          customDateTo.value = dayjs(range.end).format('YYYY-MM-DD');
          loadStats('custom');
        }
      };

      // Initial load
      onMounted(() => {
        loadStats(selectedPeriod.value);
      });

      return {
        selectedPeriod,
        loading,
        stats,
        trends,
        ytdStats,
        conversionRatePercent,
        rejectionReasons,
        reasonLabel,
        reasonPercent,
        formatReasonValue,
        formatCurrency,
        formatCount,
        handlePeriodChange,
        handleDateRangeChange,
      };
    },
  });
</script>

<style scoped lang="scss">
  .proposal-stats {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__reasons {
      padding: 1rem 1.25rem;
      background: var(--white);
      border: 1px solid var(--slate-200);
      border-radius: 0.75rem;
    }

    &__reasons-title {
      margin: 0 0 0.75rem;
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--slate-600);
    }

    &__reason {
      &:not(:last-child) {
        margin-bottom: 0.75rem;
      }
    }

    &__reason-meta {
      display: flex;
      justify-content: space-between;
      gap: 0.75rem;
      margin-bottom: 0.375rem;
      font-size: 0.8125rem;
    }

    &__reason-label {
      font-weight: 500;
      color: var(--slate-800);
    }

    &__reason-value {
      color: var(--slate-500);
      white-space: nowrap;
    }

    &__reason-track {
      height: 0.375rem;
      background: var(--slate-100);
      border-radius: 999px;
      overflow: hidden;
    }

    &__reason-fill {
      height: 100%;
      background: var(--brand-500);
      border-radius: 999px;
    }
  }
</style>
